// Calendar Integration Service for BookAPT
// Handles syncing appointments with Google Calendar, Apple Calendar, and Outlook

import SimpleStorage from '../utils/simpleStorage';

export interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  location: string;
  attendees: string[];
  reminderMinutes: number[];
  isAllDay: boolean;
  timeZone: string;
  recurrence?: {
    frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
    interval: number;
    endDate?: string;
    daysOfWeek?: number[];
  };
}

export interface CalendarAccount {
  id: string;
  name: string;
  type: 'google' | 'apple' | 'outlook' | 'yahoo';
  email: string;
  isConnected: boolean;
  lastSync: string;
}

export interface CalendarSyncResult {
  success: boolean;
  eventId?: string;
  calendarId?: string;
  error?: string;
}

class CalendarIntegrationService {
  private readonly CALENDAR_ACCOUNTS_KEY = 'calendar_accounts';
  private readonly SYNCED_EVENTS_KEY = 'synced_events';

  // Get connected calendar accounts
  async getConnectedAccounts(): Promise<CalendarAccount[]> {
    try {
      const accountsJson = await SimpleStorage.getItem(this.CALENDAR_ACCOUNTS_KEY);
      return accountsJson ? JSON.parse(accountsJson) : [];
    } catch (error) {
      console.error('Error getting connected accounts:', error);
      return [];
    }
  }

  // Connect to Google Calendar
  async connectGoogleCalendar(): Promise<CalendarSyncResult> {
    try {
      // In a real app, this would use Google Calendar API
      console.log('Connecting to Google Calendar...');
      
      // Simulate OAuth flow
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const account: CalendarAccount = {
        id: `google_${Date.now()}`,
        name: 'Google Calendar',
        type: 'google',
        email: 'user@gmail.com',
        isConnected: true,
        lastSync: new Date().toISOString()
      };

      await this.saveAccount(account);
      
      return {
        success: true,
        calendarId: account.id
      };
    } catch (error) {
      console.error('Error connecting to Google Calendar:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to connect to Google Calendar'
      };
    }
  }

  // Connect to Apple Calendar
  async connectAppleCalendar(): Promise<CalendarSyncResult> {
    try {
      // In a real app, this would use EventKit framework
      console.log('Connecting to Apple Calendar...');
      
      // Simulate connection
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const account: CalendarAccount = {
        id: `apple_${Date.now()}`,
        name: 'Apple Calendar',
        type: 'apple',
        email: 'user@icloud.com',
        isConnected: true,
        lastSync: new Date().toISOString()
      };

      await this.saveAccount(account);
      
      return {
        success: true,
        calendarId: account.id
      };
    } catch (error) {
      console.error('Error connecting to Apple Calendar:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to connect to Apple Calendar'
      };
    }
  }

  // Connect to Outlook Calendar
  async connectOutlookCalendar(): Promise<CalendarSyncResult> {
    try {
      // In a real app, this would use Microsoft Graph API
      console.log('Connecting to Outlook Calendar...');
      
      // Simulate connection
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const account: CalendarAccount = {
        id: `outlook_${Date.now()}`,
        name: 'Outlook Calendar',
        type: 'outlook',
        email: 'user@outlook.com',
        isConnected: true,
        lastSync: new Date().toISOString()
      };

      await this.saveAccount(account);
      
      return {
        success: true,
        calendarId: account.id
      };
    } catch (error) {
      console.error('Error connecting to Outlook Calendar:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to connect to Outlook Calendar'
      };
    }
  }

  // Create calendar event
  async createCalendarEvent(
    appointment: {
      id: string;
      serviceName: string;
      customerName: string;
      appointmentDate: string;
      appointmentTime: string;
      duration: number;
      businessName: string;
      businessAddress: string;
      businessPhone: string;
      notes?: string;
    },
    calendarId: string
  ): Promise<CalendarSyncResult> {
    try {
      const startDate = new Date(`${appointment.appointmentDate}T${appointment.appointmentTime}`);
      const endDate = new Date(startDate.getTime() + appointment.duration * 60000);

      const event: CalendarEvent = {
        id: `bookapt_${appointment.id}`,
        title: `${appointment.serviceName} - ${appointment.businessName}`,
        description: this.generateEventDescription(appointment),
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        location: appointment.businessAddress,
        attendees: [appointment.customerName],
        reminderMinutes: [60, 15], // 1 hour and 15 minutes before
        isAllDay: false,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      // In a real app, this would create the event in the actual calendar
      console.log('Creating calendar event:', event);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Save synced event
      await this.saveSyncedEvent(appointment.id, calendarId, event.id);
      
      return {
        success: true,
        eventId: event.id,
        calendarId
      };
    } catch (error) {
      console.error('Error creating calendar event:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create calendar event'
      };
    }
  }

  // Create recurring calendar event
  async createRecurringCalendarEvent(
    appointment: {
      id: string;
      serviceName: string;
      customerName: string;
      appointmentDate: string;
      appointmentTime: string;
      duration: number;
      businessName: string;
      businessAddress: string;
      businessPhone: string;
      notes?: string;
    },
    recurrence: {
      frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
      interval: number;
      endDate?: string;
      daysOfWeek?: number[];
    },
    calendarId: string
  ): Promise<CalendarSyncResult> {
    try {
      const startDate = new Date(`${appointment.appointmentDate}T${appointment.appointmentTime}`);
      const endDate = new Date(startDate.getTime() + appointment.duration * 60000);

      const event: CalendarEvent = {
        id: `bookapt_recurring_${appointment.id}`,
        title: `${appointment.serviceName} - ${appointment.businessName} (Recurring)`,
        description: this.generateEventDescription(appointment),
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        location: appointment.businessAddress,
        attendees: [appointment.customerName],
        reminderMinutes: [60, 15],
        isAllDay: false,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        recurrence
      };

      console.log('Creating recurring calendar event:', event);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Save synced event
      await this.saveSyncedEvent(appointment.id, calendarId, event.id);
      
      return {
        success: true,
        eventId: event.id,
        calendarId
      };
    } catch (error) {
      console.error('Error creating recurring calendar event:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create recurring calendar event'
      };
    }
  }

  // Update calendar event
  async updateCalendarEvent(
    appointmentId: string,
    updates: Partial<CalendarEvent>,
    calendarId: string
  ): Promise<CalendarSyncResult> {
    try {
      const syncedEvent = await this.getSyncedEvent(appointmentId, calendarId);
      if (!syncedEvent) {
        return {
          success: false,
          error: 'Event not found in calendar'
        };
      }

      console.log('Updating calendar event:', syncedEvent.eventId, updates);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return {
        success: true,
        eventId: syncedEvent.eventId,
        calendarId
      };
    } catch (error) {
      console.error('Error updating calendar event:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update calendar event'
      };
    }
  }

  // Delete calendar event
  async deleteCalendarEvent(
    appointmentId: string,
    calendarId: string
  ): Promise<CalendarSyncResult> {
    try {
      const syncedEvent = await this.getSyncedEvent(appointmentId, calendarId);
      if (!syncedEvent) {
        return {
          success: false,
          error: 'Event not found in calendar'
        };
      }

      console.log('Deleting calendar event:', syncedEvent.eventId);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Remove synced event
      await this.removeSyncedEvent(appointmentId, calendarId);
      
      return {
        success: true,
        eventId: syncedEvent.eventId,
        calendarId
      };
    } catch (error) {
      console.error('Error deleting calendar event:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete calendar event'
      };
    }
  }

  // Sync all appointments to calendar
  async syncAllAppointments(
    appointments: any[],
    calendarId: string
  ): Promise<CalendarSyncResult[]> {
    try {
      const results: CalendarSyncResult[] = [];
      
      for (const appointment of appointments) {
        const result = await this.createCalendarEvent(appointment, calendarId);
        results.push(result);
      }
      
      return results;
    } catch (error) {
      console.error('Error syncing all appointments:', error);
      return [{
        success: false,
        error: error instanceof Error ? error.message : 'Failed to sync appointments'
      }];
    }
  }

  // Get calendar availability
  async getCalendarAvailability(
    calendarId: string,
    startDate: string,
    endDate: string
  ): Promise<{ start: string; end: string }[]> {
    try {
      // In a real app, this would query the calendar for busy times
      console.log(`Getting availability for ${calendarId} from ${startDate} to ${endDate}`);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Return mock busy times
      return [
        {
          start: '2024-01-15T09:00:00Z',
          end: '2024-01-15T10:00:00Z'
        },
        {
          start: '2024-01-15T14:00:00Z',
          end: '2024-01-15T15:00:00Z'
        }
      ];
    } catch (error) {
      console.error('Error getting calendar availability:', error);
      return [];
    }
  }

  // Helper methods
  private generateEventDescription(appointment: any): string {
    return `
BookAPT Appointment

Service: ${appointment.serviceName}
Customer: ${appointment.customerName}
Business: ${appointment.businessName}
Phone: ${appointment.businessPhone}
Address: ${appointment.businessAddress}

${appointment.notes ? `Notes: ${appointment.notes}` : ''}

Booked via BookAPT App
    `.trim();
  }

  private async saveAccount(account: CalendarAccount): Promise<void> {
    try {
      const accounts = await this.getConnectedAccounts();
      const existingIndex = accounts.findIndex(acc => acc.id === account.id);
      
      if (existingIndex >= 0) {
        accounts[existingIndex] = account;
      } else {
        accounts.push(account);
      }
      
      await SimpleStorage.setItem(this.CALENDAR_ACCOUNTS_KEY, JSON.stringify(accounts));
    } catch (error) {
      console.error('Error saving account:', error);
    }
  }

  private async saveSyncedEvent(
    appointmentId: string,
    calendarId: string,
    eventId: string
  ): Promise<void> {
    try {
      const syncedEventsJson = await SimpleStorage.getItem(this.SYNCED_EVENTS_KEY);
      const syncedEvents = syncedEventsJson ? JSON.parse(syncedEventsJson) : {};
      
      if (!syncedEvents[appointmentId]) {
        syncedEvents[appointmentId] = {};
      }
      
      syncedEvents[appointmentId][calendarId] = {
        eventId,
        syncedAt: new Date().toISOString()
      };
      
      await SimpleStorage.setItem(this.SYNCED_EVENTS_KEY, JSON.stringify(syncedEvents));
    } catch (error) {
      console.error('Error saving synced event:', error);
    }
  }

  private async getSyncedEvent(
    appointmentId: string,
    calendarId: string
  ): Promise<{ eventId: string; syncedAt: string } | null> {
    try {
      const syncedEventsJson = await SimpleStorage.getItem(this.SYNCED_EVENTS_KEY);
      const syncedEvents = syncedEventsJson ? JSON.parse(syncedEventsJson) : {};
      
      return syncedEvents[appointmentId]?.[calendarId] || null;
    } catch (error) {
      console.error('Error getting synced event:', error);
      return null;
    }
  }

  private async removeSyncedEvent(
    appointmentId: string,
    calendarId: string
  ): Promise<void> {
    try {
      const syncedEventsJson = await SimpleStorage.getItem(this.SYNCED_EVENTS_KEY);
      const syncedEvents = syncedEventsJson ? JSON.parse(syncedEventsJson) : {};
      
      if (syncedEvents[appointmentId]?.[calendarId]) {
        delete syncedEvents[appointmentId][calendarId];
        await SimpleStorage.setItem(this.SYNCED_EVENTS_KEY, JSON.stringify(syncedEvents));
      }
    } catch (error) {
      console.error('Error removing synced event:', error);
    }
  }

  async disconnectCalendar(type: 'google' | 'outlook' | 'apple' | 'yahoo'): Promise<boolean> {
    try {
      const accounts = await this.getConnectedAccounts();
      const filtered = accounts.filter(a => a.type !== type);
      await SimpleStorage.setItem(this.CALENDAR_ACCOUNTS_KEY, JSON.stringify(filtered));
      return true;
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
      return false;
    }
  }
}

export const calendarIntegrationService = new CalendarIntegrationService();
export default CalendarIntegrationService;